import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// What this user may see and do — decided server-side (App\Http\Controllers\Api\InspectionListController).
export interface InspectionListAccess {
    scope: 'all' | 'own';                 // 'own' = inspectors: only their jobs
    role: 'administrator' | 'staff' | 'inspector';
    can: { status: boolean, delete: boolean, transactions: boolean, export: boolean, logs: boolean, create: boolean };
    inspectors: { id: number, name: string }[];
    branches: { id: number, name: string }[];
}

export interface InspectionRow {
    job_id: number;
    report_number: string;
    type: string;
    address1: string;
    address2: string;
    client: string;
    company: string;
    status: string;
    status_key: string;                   // intake / inspection / work / completed / hold / cancelled
    scheduled: string;
    inspector: string;
    agent_id: number;
    deleted: boolean;
    // KAN-88 VIP / Alert badges: the client's own flags, and flags only another party carries.
    client_badges: { vip: boolean, alert: boolean };
    property_badges: { vip: boolean, alert: boolean };
}

export interface InspectionFilters {
    status: string;                       // 'active' (default: All Active) | 'all' | 'Active - Intake' | ...
    search: string;
    agent_id: number;
    branch_id: number;
    inspection_type: string;
    date_from: string;                    // YYYY-MM-DD
    date_to: string;
    property_address: string;
    include_deleted: boolean;
    include_cancelled: boolean;
    include_on_hold: boolean;
    sort: string;
    dir: 'asc' | 'desc';
}

@Injectable({
    providedIn: 'root'
})
export class InspectionListService {
    private readonly base = environment.api + 'inspections-list';

    constructor(private http: HttpClient) { }

    // What the Inspection Overview page offers this user (change status, order logs).
    overviewAccess(): Observable<{ can: { status: boolean, logs: boolean } }> {
        return this.http.get<{ can: { status: boolean, logs: boolean } }>(this.base + '/overview-access');
    }

    access(): Observable<InspectionListAccess> {
        return this.http.get<InspectionListAccess>(this.base + '/access');
    }

    list(filters: InspectionFilters, page: number, perPage: number): Observable<{ total: number, rows: InspectionRow[] }> {
        const params = this.params(filters).set('page', String(page)).set('per_page', String(perPage));
        return this.http.get<{ total: number, rows: InspectionRow[] }>(this.base, { params });
    }

    // The CSV comes back as a file for this user only (no shared export file).
    export(filters: InspectionFilters): Observable<HttpResponse<Blob>> {
        return this.http.get(this.base + '/export', { params: this.params(filters), responseType: 'blob', observe: 'response' });
    }

    changeStatus(jobId: number, status: string, notes: string): Observable<any> {
        return this.http.post(this.base + '/' + jobId + '/status', { job_status_top: status, job_status_notes: notes });
    }

    delete(jobId: number): Observable<any> {
        return this.http.post(this.base + '/' + jobId + '/delete', {});
    }

    private params(f: InspectionFilters): HttpParams {
        let p = new HttpParams().set('status', f.status).set('sort', f.sort).set('dir', f.dir);
        const optional: { [key: string]: any } = {
            search: f.search.trim(), agent_id: f.agent_id, branch_id: f.branch_id, inspection_type: f.inspection_type,
            date_from: f.date_from, date_to: f.date_to, property_address: f.property_address.trim(),
            include_deleted: f.include_deleted ? 1 : 0, include_cancelled: f.include_cancelled ? 1 : 0, include_on_hold: f.include_on_hold ? 1 : 0
        };
        Object.keys(optional).forEach(key => {
            if (optional[key]) {
                p = p.set(key, String(optional[key]));
            }
        });
        return p;
    }
}
